/**
 * The time an AMHP asks for, as time ranges on dates.
 *
 * A range is one merged run of painted time on a single date; nothing spans
 * midnight, because the wall-clock model has no way to say so. Every `now`
 * these functions take is UK wall clock (`dayjs().tz(UK_TIME_ZONE)`): the
 * conversions between ranges and instants resolve in that zone, so a `now`
 * read in the browser's own zone would put the default request on the wrong
 * date for anyone abroad.
 */
import { err, ok, Result } from "neverthrow";

import type {
  Dayjs,
  InstantRange,
  InvalidError,
  TimeRange,
  TimeRangeOnDate,
} from "@thalamos/common";
import {
  buildInvalidError,
  dayjs,
  formatClockTime,
  formatTimeOfDay,
  instantsToTimeRangesOnDate,
  ISO_DATE_FORMAT,
  mergeTimeRangesOnDate,
  parseTimeOfDay,
  timeRangeOnDateToInstants,
  timeRangesEqual,
} from "@thalamos/common";
import { ASSESSMENT_DURATION_MINUTES } from "@thalamos/aspire-common";

const QUARTER_HOUR_MINUTES = 15;

/**
 * How far ahead the request reaches when nobody has said otherwise. Long
 * enough to cover the rest of a working session without an AMHP painting
 * anything, short enough that it is plainly a starting point rather than an
 * answer.
 */
const DEFAULT_REQUEST_HOURS = 6;

/** A range that cannot be asked for, and why. */
export type UnusableTimeRangeOnDate = TimeRangeOnDate & {
  /**
   * `"short"` — less than one assessment long. `"nonexistent"` — wall-clock
   * time the spring transition skips, which occupies no instants at all.
   */
  reason: "short" | "nonexistent";
};

/** Nominal wall-clock length, which on a transition day is not the real one. */
function lengthMinutes(range: TimeRange): number {
  return parseTimeOfDay(range.until) - parseTimeOfDay(range.from);
}

/**
 * The ranges that cannot be asked for, merged first because it is the whole
 * range that has to qualify rather than any piece of it.
 *
 * Nonexistent is reported ahead of short: a range inside the skipped hour may
 * be both, and no amount of extending it within that hour would help.
 */
export function unusableRanges(
  ranges: readonly TimeRangeOnDate[],
): UnusableTimeRangeOnDate[] {
  return mergeTimeRangesOnDate(ranges).flatMap<UnusableTimeRangeOnDate>(
    (range) => {
      if (timeRangeOnDateToInstants(range) === undefined) {
        return [{ ...range, reason: "nonexistent" }];
      }
      if (lengthMinutes(range) < ASSESSMENT_DURATION_MINUTES) {
        return [{ ...range, reason: "short" }];
      }
      return [];
    },
  );
}

/**
 * What an AMHP is asking for before they have asked for anything: from the
 * quarter hour they are in, forward {@link DEFAULT_REQUEST_HOURS} hours.
 *
 * Rounded down to the quarter hour rather than subtracting the remainder,
 * because the seconds and milliseconds have to go too — an instant that is not
 * a whole minute has no range to convert into. The quarter hour in progress is
 * included because it is still offerable, and a tail left by the split at
 * midnight is dropped when it is shorter than an assessment, so the default
 * never opens onto a range the AMHP is told to fix.
 */
export function defaultRequestedRanges(now: Dayjs): TimeRangeOnDate[] {
  const start = now
    .startOf("hour")
    .add(
      Math.floor(now.minute() / QUARTER_HOUR_MINUTES) * QUARTER_HOUR_MINUTES,
      "minute",
    );
  // Counted on the timeline rather than on the wall clock, and so taken in UTC:
  // on the days the clocks change, six hours of wall clock are not six hours.
  const request: InstantRange = {
    start: start.toISOString(),
    end: dayjs
      .utc(start.valueOf())
      .add(DEFAULT_REQUEST_HOURS, "hour")
      .toISOString(),
  };

  // Both ends are whole quarter hours, so the conversion has nothing to reject.
  const split = instantsToTimeRangesOnDate([request]).unwrapOr([]);
  return mergeTimeRangesOnDate(split).filter(
    (range) => lengthMinutes(range) >= ASSESSMENT_DURATION_MINUTES,
  );
}

/**
 * A range widened to the quarter hours it touches. Idempotent on a range that
 * already sits on them.
 *
 * The grid rounds inward when it reads a range back, so an unaligned one
 * shrinks each time it is shown and one under a quarter hour vanishes. Widening
 * first is what keeps a time agreed elsewhere intact on the grid. The end needs
 * no cap: it arrives no later than midnight, which is itself a quarter hour.
 */
export function snapOutwardToQuarterHour(
  range: TimeRangeOnDate,
): TimeRangeOnDate {
  return {
    date: range.date,
    from: formatTimeOfDay(
      Math.floor(parseTimeOfDay(range.from) / QUARTER_HOUR_MINUTES) *
        QUARTER_HOUR_MINUTES,
    ),
    until: formatTimeOfDay(
      Math.ceil(parseTimeOfDay(range.until) / QUARTER_HOUR_MINUTES) *
        QUARTER_HOUR_MINUTES,
    ),
  };
}

/** Milliseconds since the epoch, for ordering and comparing instants. */
function instant(iso: string): number {
  return dayjs(iso).valueOf();
}

/**
 * Instants as the ranges a grid can show them as: split at midnight, widened
 * to the quarter hour, then merged. Fails on an instant that is not a whole
 * number of minutes, which no range could represent faithfully.
 *
 * Widening moves a time somebody agreed, so `widened` reports those ranges as
 * they were saved: unmerged, and not paired with what they widened to, which
 * merging can fold into a neighbour.
 */
export function requestFromInstants(ranges: readonly InstantRange[]): Result<
  {
    /** What the grid shows. */
    ranges: TimeRangeOnDate[];
    /** The saved ranges, as saved, that do not sit on the quarter hour. */
    widened: TimeRangeOnDate[];
  },
  InvalidError
> {
  return instantsToTimeRangesOnDate(ranges).map((dated) => {
    const snapped: TimeRangeOnDate[] = [];
    // Keyed so the same saved time appearing twice is reported once.
    const widened = new Map<string, TimeRangeOnDate>();
    for (const range of dated) {
      const onGrid = snapOutwardToQuarterHour(range);
      snapped.push(onGrid);
      if (!timeRangesEqual(range, onGrid)) {
        widened.set(formatTimeRangeOnDate(range), range);
      }
    }
    return {
      ranges: mergeTimeRangesOnDate(snapped),
      widened: [...widened.values()],
    };
  });
}

/**
 * A request as the instants it occupies, for storing or sending.
 *
 * A range that runs past midnight exists on the grid as two, one per date,
 * because no wall-clock range crosses a day. The instants have no such limit,
 * so touching ranges are re-joined here: the split was the grid's, not the
 * request's, and a calendar invitation wants the whole visit as one span.
 *
 * Fails on a range naming time that does not exist. Callers are expected to
 * have found {@link unusableRanges} empty first, so the failure marks a
 * programming error rather than bad input — but it is still reported, because
 * dropping the range quietly would lose time somebody asked for.
 */
export function requestToInstants(
  ranges: readonly TimeRangeOnDate[],
): Result<InstantRange[], InvalidError> {
  return Result.combine(
    mergeTimeRangesOnDate(ranges).map((range) => {
      const converted = timeRangeOnDateToInstants(range);
      return converted === undefined
        ? err(
            buildInvalidError({
              message: `${formatTimeRangeOnDate(range)} names time that does not exist on that date`,
            }),
          )
        : ok(converted);
    }),
  ).map((instants) =>
    // Sorted here rather than trusting the order they arrived in: joining
    // neighbours only works on a timeline.
    [...instants]
      .sort((left, right) => instant(left.start) - instant(right.start))
      .reduce<InstantRange[]>((joined, range) => {
        const previous = joined.at(-1);
        if (
          previous !== undefined &&
          instant(previous.end) === instant(range.start)
        ) {
          joined[joined.length - 1] = { start: previous.start, end: range.end };
        } else {
          joined.push(range);
        }
        return joined;
      }, []),
  );
}

/**
 * A range as `"Tue 8 Sep 13:00-19:00"`. Parsed in UTC so the zoneless date is
 * not reinterpreted in the browser's zone.
 */
export function formatTimeRangeOnDate(range: TimeRangeOnDate): string {
  const day = dayjs.utc(range.date, ISO_DATE_FORMAT).format("ddd D MMM");
  return `${day} ${formatClockTime(range.from)}-${formatClockTime(range.until)}`;
}

/**
 * A whole request in as few words as a button can hold: the one range it asks
 * for, or how many there are. What no time at all should read as is the
 * caller's to say, because only it knows what an empty request means.
 */
export function formatRequestSummary(
  ranges: readonly TimeRangeOnDate[],
): string {
  const merged = mergeTimeRangesOnDate(ranges);
  const only = merged.length === 1 ? merged[0] : undefined;
  if (only !== undefined) {
    return formatTimeRangeOnDate(only);
  }
  return `${String(merged.length)} time ranges`;
}
